import Image from "next/image";
import Link from "next/link";
import { useRef } from "react";
import Seo from "../../components/Seo";
import { useLineQuoteUrl } from "lib/line";

const idealClients = [
  {
    title: "ธุรกิจที่มีเอกสารและแบบภาษีต้องจัดการทุกเดือน",
    description:"เหมาะกับกิจการที่ต้องการส่งเอกสารตามรอบและติดตามรายงานทางบัญชีอย่างต่อเนื่อง"
  },
  {
    title: "บริษัทที่ต้องการยื่นแบบภาษีตรงเวลา และเตรียมงบให้ผู้บริหาร",
    description:"สำหรับธุรกิจที่เริ่มมีพนักงาน มีภาษีหัก ณ ที่จ่าย และต้องตรวจสอบความครบถ้วนก่อนยื่น ภ.พ.30 / ภงด.1/3/53"
  },
  {
    title: "เจ้าของกิจการที่ต้องการจัดระเบียบเอกสารและภาษี",
    description:"ช่วยกำหนดขั้นตอนเอกสาร ตรวจความครบถ้วน และเตรียมข้อมูลสำหรับการยื่นแบบที่เกี่ยวข้อง"
  }
];

const monthlyServices = [
  "จัดวางระบบบัญชีและเอกสารควบคุมภายในที่ใช้งานจริงกับทีมของคุณ",
  "ตรวจรายการทางภาษีและแจ้งข้อมูลที่กิจการควรเตรียมเพิ่มเติม",
  "ตรวจเอกสารก่อนบันทึกให้ครบ ถูก ต้องหัก ณ ที่จ่าย และแนบเอกสารประกอบ",
  "จัดทำรายงานภาษีซื้อ–ขาย และแบบ ภ.พ.30, ภ.ง.ด.1/3/53 หรือ ภ.พ.36 ตามที่เกี่ยวข้อง",
  "บันทึกบัญชีจากเอกสารที่กิจการส่งให้ตามรอบที่กำหนด",
  "จัดทำสมุดบัญชีครบถ้วนตามที่กฎหมายกำหนด",
  "ประมาณการ ภงด.51 กลางปี และให้คำแนะนำเพื่อปรับแผนภาษี",
  "เตรียมข้อมูลประกอบการปิดบัญชีประจำปีตามขอบเขตที่เสนอราคาแยก"
];

const monthlyTimeline = [
  { title: "วันที่ 1–5", detail: "ลูกค้าส่งเอกสารเดือนก่อน เช่น ใบกำกับ ใบเสร็จ สลิป โอนเงิน สัญญา และสต็อก" },
  { title: "วันที่ 6–13", detail: "ทีมตรวจเอกสาร บันทึกบัญชี แจ้งรายการตกหล่น และสรุปรายงานภาษีหัก ณ ที่จ่ายเพื่อยืนยันก่อนยื่น" },
  { title: "วันที่ 15–19", detail: "สรุปรายงานภาษีซื้อ–ขาย, ภ.พ.30, ภงด.1/3/53 และ ภพ.36 พร้อมส่งใบแจ้งเตือนชำระ" },
  { title: "วันที่ 20–23", detail: "ยื่น ภ.พ.30 และส่งใบเตือนชำระยอดภาษี" },
  { title: "ปลายเดือน", detail: "ส่งรายงานสรุปผู้บริหาร (กำไร–ขาดทุน, กระแสเงินสด และสถานะภาษีที่เกี่ยวข้อง)" }
];

const clientChecklist = [
  "เอกสารขาย–ซื้อทั้งเดือน, สลิปโอน/เช็ค, รายการรับ–จ่าย, สัญญา และข้อมูลสต็อก (ถ้ามี)",
  "สิทธิ์เข้าถึงระบบ e-Tax และ RD e-Filing (ถ้ามี)",
  "ข้อมูลพนักงานสำหรับการยื่น ภงด.1 และประกันสังคม"
];

const assurancePoints = [
  "แจ้งรายการเอกสารและขอบเขตงานก่อนเริ่มบริการ",
  "กำหนดผู้ติดต่อ ช่องทางสื่อสาร และรอบส่งงานร่วมกัน",
  "ตรวจความครบถ้วนของข้อมูลก่อนจัดทำแบบภาษีและรายงาน"
];

const excludedServices = [
  "งานบัญชีย้อนหลังหรือแก้ไขข้อมูลจากรอบก่อนเริ่มบริการ",
  "ค่าปิดบัญชีประจำปี ค่าสอบบัญชี และค่าธรรมเนียมของหน่วยงานหรือบุคคลภายนอก",
  "งานจดทะเบียน เปลี่ยนแปลงข้อมูลบริษัท และงานภาษีเฉพาะกรณี",
  "เอกสารหรือรายการที่เกินจำนวนของแพ็กเกจที่ตกลง"
];

const heroImage = {
  src: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1380&q=80",
  alt: "ทีมบัญชีหารือรายละเอียดเอกสารกับลูกค้า",
  width: 1380,
  height: 1040
};

const navigationItems = [
  { id: "fit", label: "เหมาะกับใคร" },
  { id: "monthly-services", label: "บริการประจำเดือน" },
  { id: "timeline", label: "เส้นเวลา" },
  { id: "checklist", label: "สิ่งที่ต้องเตรียม" },
  { id: "standards", label: "มาตรฐาน" },
  { id: "pricing", label: "ค่าบริการ" },
  { id: "not-included", label: "งานที่ไม่รวม" },
  { id: "faq", label: "คำถามที่พบบ่อย" }
];

const pricingPackages = [
  {
    name: "ธุรกิจเริ่มต้น",
    note: "กิจการขนาดเล็กที่มีเอกสารไม่มาก",
    records: "ไม่เกิน 30 รายการ/เดือน",
    price: "2,000",
    unit: "บาทต่อเดือน",
    features: ["บันทึกบัญชีจากเอกสารที่ได้รับ", "จัดทำแบบภาษีที่เกี่ยวข้อง", "สรุปยอดขายและค่าใช้จ่ายประจำเดือน"],
    highlight: false,
  },
  {
    name: "ธุรกิจจด VAT",
    note: "กิจการที่มีภาษีซื้อ–ขายและแบบภาษีหลายประเภท",
    records: "ประเมินตามจำนวนเอกสาร",
    price: "สอบถามราคา",
    unit: "ตามขอบเขต",
    features: ["ตรวจเอกสารภาษีซื้อ–ขาย", "จัดทำแบบภาษีตามหน้าที่ของกิจการ", "กำหนดรอบส่งเอกสารและผู้ติดต่อ"],
    highlight: true,
  },
  {
    name: "ธุรกิจเอกสารมาก",
    note: "กิจการที่มีพนักงาน หลายช่องทางขาย หรือหลายสาขา",
    records: "ประเมินจากตัวอย่างเอกสาร",
    price: "สอบถามราคา",
    unit: "ตามขอบเขต",
    features: ["ประเมินปริมาณและความซับซ้อนของรายการ", "กำหนดรูปแบบรายงานร่วมกัน", "แจ้งงานเพิ่มเติมก่อนดำเนินการ"],
  }
];

export default function AccountingServicesPage() {
  const title = "รับทำบัญชีรายเดือนและยื่นภาษี เชียงใหม่";
  const lineUrl = useLineQuoteUrl();
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://www.kmcpaccounting.com").replace(/\/$/, "");
  const pageUrl = `${siteUrl}/services/accounting`;
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าแรก", item: `${siteUrl}/` },
          { "@type": "ListItem", position: 2, name: "บริการทำบัญชีรายเดือน", item: pageUrl }
        ]
      },
      {
        "@type": "Service",
        "@id": `${pageUrl}#service`,
        name: "บริการทำบัญชีรายเดือนและยื่นภาษี",
        serviceType: "รับทำบัญชีรายเดือนและจัดทำแบบภาษีที่เกี่ยวข้อง",
        url: pageUrl,
        provider: { "@id": `${siteUrl}/#organization` },
        areaServed: { "@type": "AdministrativeArea", name: "เชียงใหม่" },
        offers: {
          "@type": "Offer",
          price: "2000",
          priceCurrency: "THB",
          description: "ค่าบริการเริ่มต้นสำหรับกิจการที่มีเอกสารไม่เกิน 30 รายการต่อเดือน โดยยืนยันราคาหลังประเมินขอบเขตงาน"
        }
      }
    ]
  };

  const handleScroll = (direction: "next" | "prev") => {
    if (typeof window === "undefined") {
      return;
    }
    const container = sliderRef.current;
    if (!container) {
      return;
    }
    const scrollAmount = container.clientWidth * (window.innerWidth >= 1024 ? 0.9 : 1);
    const offset = direction === "next" ? scrollAmount : -scrollAmount;
    container.scrollBy({ left: offset, behavior: "smooth" });
  };

  return (
    <>
      <Seo
        title={title}
        description="บริการรับทำบัญชีรายเดือนในเชียงใหม่ เริ่มต้น 2,000 บาท สำหรับเอกสารไม่เกิน 30 รายการ ตรวจเอกสาร บันทึกบัญชี จัดทำแบบภาษี และสรุปรายงานตามขอบเขต"
        path="/services/accounting"
        jsonLd={structuredData}
      />
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-20 -left-24 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
          <div className="absolute bottom-10 right-0 h-64 w-64 rounded-full bg-gray-200/60 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 pt-14 pb-16 sm:px-8 sm:pt-16 sm:pb-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.05fr,0.95fr] lg:gap-12">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-brand">
                บริการบัญชีและภาษี
              </p>
              <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-[1.24] text-gray-900 sm:leading-[1.18]">
                รับทำบัญชีรายเดือนและยื่นภาษีในเชียงใหม่
              </h1>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-gray-600">
                ตรวจเอกสาร บันทึกบัญชี จัดทำแบบภาษี และสรุปข้อมูลประจำเดือนตามหน้าที่ของกิจการ โดยแจ้งขอบเขต รอบส่งเอกสาร และค่าบริการก่อนเริ่มงาน
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a href={lineUrl} target="_blank" rel="noreferrer" className="btn-primary">
                  นัดคุยประเมินฟรี
                </a>
                <Link href="#pricing" className="btn-secondary">
                  ดูแพ็กเกจราคา
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-24 right-10 hidden h-56 w-56 rounded-full bg-white/40 blur-3xl lg:block" />
              <div className="absolute -bottom-12 left-0 hidden h-64 w-64 rounded-full bg-brand/25 blur-3xl lg:block" />
              <div className="relative overflow-hidden rounded-[2.5rem] border border-white/60 bg-white/80 shadow-soft">
                <Image
                  src={heroImage.src}
                  alt={heroImage.alt}
                  width={heroImage.width}
                  height={heroImage.height}
                  className="h-full w-full object-cover"
                  priority
                  sizes="(min-width: 1024px) 540px, 92vw"
                />
              </div>
            </div>
          </div>

          <nav className="mt-10 overflow-x-auto" aria-label="สารบัญบริการบัญชีและภาษี">
            <ul className="flex items-center gap-3 pb-2">
              {navigationItems.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-2 text-sm font-semibold text-gray-600 transition hover:border-brand hover:text-brand"
                  >
                    <span className="h-2 w-2 rounded-full bg-brand/60" />
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div id="pricing" className="mt-12 scroll-mt-28">
            <div className="mx-auto max-w-6xl px-6 text-center sm:px-8">
              <h2 className="text-3xl font-semibold leading-[1.25] tracking-tight text-gray-900 sm:text-4xl">
                ค่าบริการบัญชีรายเดือนเบื้องต้น
              </h2>
              <p className="mx-auto mt-4 max-w-3xl text-base text-gray-600 sm:text-lg">
                ราคาเริ่มต้น 2,000 บาทต่อเดือนสำหรับเอกสารไม่เกิน 30 รายการ ราคาจริงพิจารณาจากประเภทธุรกิจ ปริมาณเอกสาร จำนวนพนักงาน และแบบภาษีที่ต้องจัดทำ
              </p>
            </div>

            <div className="relative mx-auto mt-9 max-w-6xl px-6 sm:px-8">
              <button
                type="button"
                onClick={() => handleScroll("prev")}
                className="absolute left-0 top-1/2 hidden -translate-y-1/2 rounded-full border border-gray-200 bg-white p-3 text-gray-600 shadow-lg transition hover:border-brand hover:text-brand lg:inline-flex"
                aria-label="แสดงแพ็กเกจก่อนหน้า"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M12.79 4.21a.75.75 0 01.02 1.06L8.832 9.5l3.978 4.23a.75.75 0 01-1.08 1.04l-4.5-4.789a.75.75 0 010-1.04l4.5-4.79a.75.75 0 011.06-.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => handleScroll("next")}
                className="absolute right-0 top-1/2 hidden -translate-y-1/2 rounded-full border border-gray-200 bg-white p-3 text-gray-600 shadow-lg transition hover:border-brand hover:text-brand lg:inline-flex"
                aria-label="แสดงแพ็กเกจถัดไป"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M7.21 15.79a.75.75 0 01-.02-1.06l3.978-4.23-3.978-4.23a.75.75 0 011.08-1.04l4.5 4.789a.75.75 0 010 1.04l-4.5 4.79a.75.75 0 01-1.06.02z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <div
                ref={sliderRef}
                className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 lg:overflow-x-hidden lg:overflow-y-visible"
              >
                {pricingPackages.map((pkg) => {
                  const isHighlight = pkg.highlight;
                  return (
                    <div
                      key={pkg.name}
                      className={`min-w-[85%] snap-start rounded-2xl border bg-white p-6 shadow-[0_25px_60px_-35px_rgba(15,23,42,0.25)] transition hover:shadow-[0_30px_65px_-30px_rgba(15,23,42,0.27)] sm:min-w-[55%] sm:p-7 lg:min-w-[33%] xl:min-w-[31%] 2xl:min-w-[28%] ${
                        isHighlight ? "border-brand ring-2 ring-brand/30" : "border-gray-100"
                      }`}
                    >
                      {isHighlight ? (
                        <span className="mb-3 inline-flex items-center rounded-full bg-gray-100 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700">
                          แพ็กเกจยอดนิยม
                        </span>
                      ) : null}
                      <h3 className="text-base font-semibold uppercase tracking-widest text-brand/90">{pkg.name}</h3>
                      <p className="mt-2 text-sm text-gray-600">{pkg.note}</p>
                      <div className="mt-6 flex items-baseline gap-2">
                        <span className="text-4xl font-semibold text-gray-900">{pkg.price}</span>
                        <span className="text-sm text-gray-500">{pkg.unit}</span>
                      </div>
                      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {pkg.records}
                      </p>
                      <ul className="mt-6 space-y-3 text-sm text-gray-700">
                        {pkg.features.map((feature) => (
                          <li key={feature} className="flex gap-3">
                            <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${
                              isHighlight ? "bg-brand text-white" : "bg-brand text-white"
                            }`}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                  fillRule="evenodd"
                                  d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <a
                        href={lineUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={`mt-8 inline-flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold transition ${
                          isHighlight ? "bg-brand text-white hover:bg-brand/90" : "border border-gray-200 text-gray-700 hover:border-brand hover:text-brand"
                        }`}
                      >
                        สนใจแพ็กเกจนี้
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <section id="not-included" className="mt-10 scroll-mt-28 rounded-2xl border border-amber-200 bg-amber-50/60 p-6 sm:p-8">
            <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">งานที่ไม่รวมในราคาเริ่มต้น</h2>
            <p className="mt-3 max-w-3xl text-base leading-7 text-gray-600">
              รายการต่อไปนี้ต้องตรวจข้อมูลและประเมินค่าใช้จ่ายแยก ทีมงานจะแจ้งรายละเอียดให้ยืนยันก่อนดำเนินการ
            </p>
            <ul className="mt-6 grid gap-3 text-sm leading-7 text-gray-700 sm:grid-cols-2">
              {excludedServices.map((item) => (
                <li key={item} className="rounded-2xl border border-amber-200/80 bg-white px-5 py-4">{item}</li>
              ))}
            </ul>
            <p className="mt-5 text-sm leading-7 text-gray-600">
              ใบเสนอราคาจะระบุด้วยว่ารวมค่าธรรมเนียมราชการ ค่าสอบบัญชี และค่าใช้จ่ายบุคคลภายนอกหรือไม่
            </p>
          </section>

          <div id="fit" className="mt-12 scroll-mt-28">
            <div className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-[0_40px_80px_-60px_rgba(15,23,42,0.35)] sm:p-8 lg:p-10">
              <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-brand/10 blur-3xl" aria-hidden="true" />
              <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-indigo-100/40 blur-3xl" aria-hidden="true" />
              <div className="relative">
                <span className="text-xs font-semibold uppercase tracking-[0.4em] text-brand/70">Who we help</span>
                <h2 className="mt-3 text-3xl font-semibold text-gray-900 sm:text-4xl">เหมาะกับใคร</h2>
                <p className="mt-3 max-w-3xl text-sm text-gray-600 sm:text-base">
                  เหมาะกับกิจการที่ต้องการจัดระบบเอกสาร ดูแลงานบัญชีตามรอบ และเตรียมแบบภาษีจากข้อมูลที่ส่งให้
                </p>

                <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                  {idealClients.map((item, index) => {
                    const badges = ["bg-brand text-white", "bg-indigo-100 text-indigo-600", "bg-amber-100 text-amber-600"];
                    return (
                      <div
                        key={item.title}
                        className="rounded-2xl border border-white/60 bg-white/90 px-6 py-6 text-left shadow-[0_25px_55px_-30px_rgba(15,23,42,0.25)] transition hover:-translate-y-1 hover:shadow-[0_30px_70px_-35px_rgba(15,23,42,0.25)]"
                      >
                        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${badges[index % badges.length]}`}>
                          {index + 1}
                        </span>
                        <h3 className="mt-4 text-lg font-semibold text-gray-900">{item.title}</h3>
                        <p className="mt-2 text-sm text-gray-600">{item.description}</p>
                      </div>
                    );
                  })}
                </div>

              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-[1.2fr,0.9fr]">
            <section id="monthly-services" className="scroll-mt-28 rounded-2xl border border-gray-100 bg-white p-6 shadow-soft sm:p-8">
              <div className="mx-auto max-w-3xl text-center">
                <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">บริการประจำเดือนที่คุณได้รับ</h2>
                <p className="mt-3 text-sm text-gray-600 sm:text-base">
                  ขอบเขตจริงขึ้นอยู่กับหน้าที่ทางภาษีและแพ็กเกจที่ตกลง โดยทีมงานจะแจ้งรายการงานก่อนเริ่มบริการ
                </p>
              </div>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {monthlyServices.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-5 py-4 text-sm text-gray-700"
                  >
                    <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand/15 text-brand">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 rounded-2xl border border-brand/15 bg-brand/5 px-6 py-5 text-sm text-gray-700">
                <p>
                  ช่องทางติดต่อ รอบตอบกลับ และการประชุมสรุปงานจะกำหนดตามแพ็กเกจและขอบเขตบริการที่ตกลงร่วมกัน
                </p>
                <a
                  href="https://www.rd.go.th/rdeservice/63813.html"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-flex min-h-11 items-center font-semibold text-gray-900 hover:underline"
                >
                  ตรวจสอบแบบภาษีและบริการยื่นแบบจากกรมสรรพากร →
                </a>
              </div>
            </section>

            <section id="timeline" className="scroll-mt-28 rounded-2xl border border-gray-100 bg-white p-6 shadow-soft sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">เส้นเวลา (ตัวอย่างรอบเดือน)</h2>
              <p className="mt-3 text-sm text-gray-600 sm:text-base">
                ตัวอย่างนี้ใช้สำหรับวางแผนร่วมกัน กำหนดส่งจริงอาจต่างกันตามประเภทแบบภาษี วันหยุด และวันที่ได้รับเอกสารครบ
              </p>
              <div className="relative mt-8 space-y-6">
                <span className="absolute left-3 top-2 h-[calc(100%-12px)] w-px bg-gradient-to-b from-brand/40 to-transparent" />
                {monthlyTimeline.map((item, index) => (
                  <div key={item.title} className="relative pl-10">
                    <span className="absolute left-0 top-1 inline-flex h-3 w-3 items-center justify-center rounded-full border-4 border-white bg-brand shadow-[0_0_0_2px_rgba(59,130,246,0.15)]" />
                    <h3 className="text-sm font-semibold text-brand">{item.title}</h3>
                    <p className="mt-2 text-sm text-gray-600">{item.detail}</p>
                    {index !== monthlyTimeline.length - 1 ? (
                      <span className="absolute left-3 top-5 h-[calc(100%-16px)] w-px bg-gray-200" />
                    ) : null}
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <section id="checklist" className="scroll-mt-28 rounded-2xl border border-gray-100 bg-white p-6 shadow-soft sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">สิ่งที่ลูกค้าต้องเตรียม</h2>
              <p className="mt-3 text-sm text-gray-600 sm:text-base">
                รายการหลักที่ต้องมีสำหรับเริ่มต้นใช้งาน และระบบที่เราช่วยตั้งค่าให้ในวันแรก
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {clientChecklist.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-700"
                  >
                    <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand/15 text-brand">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>

            <section id="standards" className="scroll-mt-28 rounded-2xl border border-brand/15 bg-brand/5 p-6 shadow-soft sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">แนวทางการทำงาน</h2>
              <p className="mt-3 text-sm text-gray-600 sm:text-base">
                ทีมงานกำหนดรายการเอกสาร ผู้รับผิดชอบ และรอบส่งงานให้ตรวจสอบสถานะได้ชัดเจน
              </p>
              <div className="mt-6 space-y-4">
                {assurancePoints.map((item, index) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 rounded-2xl border border-white/60 bg-white/80 px-5 py-4 text-sm text-gray-700 shadow-[0_18px_40px_-30px_rgba(15,23,42,0.25)]"
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white text-xs font-semibold">
                      {index + 1}
                    </span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div id="faq" className="mt-12 grid gap-8 lg:grid-cols-[1.6fr_1fr] scroll-mt-28">
            <div className="rounded-2xl border border-gray-100 p-6 shadow-soft sm:p-8">
              <h2 className="text-2xl font-semibold text-gray-900">คำถามที่พบบ่อย</h2>
              <dl className="mt-6 space-y-6 text-base text-gray-600">
                <div>
                  <dt className="font-semibold text-gray-900">ต้องส่งเอกสารอย่างไรถ้าอยู่ต่างจังหวัด?</dt>
                  <dd className="mt-2">
                    สามารถส่งสำเนาเอกสารผ่านช่องทางออนไลน์ที่ตกลงกับทีมงาน ส่วนเอกสารต้นฉบับให้นัดหมายหรือจัดส่งตามความเหมาะสมของพื้นที่
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-gray-900">ถ้าเริ่มงานกลางเดือนจะคิดค่าบริการอย่างไร?</dt>
                  <dd className="mt-2">
                    เราประเมินจากจำนวนเอกสารจริงในเดือนเริ่มต้น และคิดค่าบริการตามสัดส่วน พร้อมแจ้งให้ทราบก่อนเริ่มงาน
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold text-gray-900">สามารถช่วยจัดระบบบัญชีย้อนหลังได้หรือไม่?</dt>
                  <dd className="mt-2">
                    ได้ ทีมเราช่วยจัดระเบียบเอกสาร ตรวจสอบความครบถ้วน และทำ Reconcile เพื่อให้ข้อมูลพร้อมก่อนส่งรายงานรายเดือน
                  </dd>
                </div>
              </dl>
            </div>
            <aside className="rounded-2xl border border-brand/20 bg-brand/5 p-6 shadow-soft sm:p-8">
              <h2 className="text-xl font-semibold text-brand">อยากย้ายสำนักงานบัญชี?</h2>
              <p className="mt-3 text-base text-gray-700">
                ทีมงานจะตรวจรายการเอกสารและงานคงค้างจากสำนักงานเดิมก่อนประเมินระยะเวลาและค่าใช้จ่ายในการรับช่วงงาน
              </p>
              <p className="mt-4 text-sm text-gray-500">
                ทีมงานจะส่งรายการข้อมูลที่ต้องรับช่วงและกำหนดรอบเริ่มงานหลังตรวจสถานะบัญชีเดิม
              </p>
              <a href={lineUrl} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center text-sm font-semibold text-brand">
                ติดต่อทีมบัญชี &rarr;
              </a>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}
